import { Trans, useLingui } from '@lingui/react/macro';
import { useMemo } from 'react';

import { Typography } from '@remarkable/ark-web';

import { useStripeSubscription, useSubscriptionMembers } from 'src/api/queries';
import { Spinner } from 'src/components';
import { FlexDivider } from 'src/components/FlexDivider';
import { TextWithLabel } from 'src/components/TextWithLabel';
import { isBillableSeat } from 'src/utils/isBillableSeat';
import { subscriptionHelpers } from 'src/utils/subscriptionHelpers';
import { subscriptionHasPaymentMethod } from 'src/utils/subscriptionPayment';

import { formatDate } from '../../../../utils/formatDate';
import { useFormattedPaymentInfo } from '../utils/getFormattedPaymentInfo';

export const NextBillingDetails = () => {
  const { t, i18n } = useLingui();
  const subscription = useStripeSubscription();
  const members = useSubscriptionMembers();
  const cardDetails = useFormattedPaymentInfo(subscription.data);
  const billableSeats = useMemo(
    () => members.data?.filter((member) => isBillableSeat(member.seat)) ?? [],
    [members.data]
  );

  if (subscription.isPending) {
    return <Spinner />;
  }
  if (subscription.isError || !subscription.data) {
    return (
      <Typography variant="body-md">
        <Trans>No billing details available</Trans>
      </Typography>
    );
  }

  if (subscriptionHelpers.isCancelled(subscription.data)) {
    const cancellationDate = formatDate(
      subscription.data.paymentInformation.cancellationAt,
      { locale: i18n.locale }
    );
    return (
      <Typography variant="body-md">
        <Trans>
          Your subscription has been cancelled and will expire on{' '}
          {cancellationDate}
        </Trans>
      </Typography>
    );
  }

  if (!subscriptionHasPaymentMethod(subscription.data)) {
    return (
      <Typography variant="body-md">
        <Trans>No billing details available</Trans>
      </Typography>
    );
  }

  return (
    <div className="flex w-full flex-col flex-wrap gap-16 lm:flex-row">
      <TextWithLabel label={t`Date`} className="flex-1">
        {cardDetails?.nextBilling}
      </TextWithLabel>

      <FlexDivider />

      <TextWithLabel label={t`Subscription`} className="flex-1">
        <Trans>Connect</Trans>
      </TextWithLabel>

      <FlexDivider />

      {subscriptionHelpers.isManagementTypeSeats(subscription.data) && (
        <>
          <TextWithLabel
            data-cy="seats-on-next-billing"
            label={t`Seats`}
            className="flex-1"
          >
            {billableSeats.length}
          </TextWithLabel>

          <FlexDivider />
        </>
      )}

      <TextWithLabel label={t`Amount`} className="flex-1">
        {cardDetails?.nextBillingAmount}
      </TextWithLabel>
    </div>
  );
};
